import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import * as calls from "./callSdk";
import * as sounds from "./sounds";
import { fetchVisibleUsers, emailForUid } from "./directoryScope";
import { parseMeta } from "./status";
import { formatDuration } from "./format";
import Avatar from "./Avatar";
import {
  PersonPlusIcon,
  PhoneIcon,
  VideoIcon,
  ChevronIcon,
  MicIcon,
  MicOffIcon,
  CameraOffIcon,
  ChatIcon,
  SendIcon,
} from "./Icons";

/** Where a call's ephemeral chat is broadcast — the group for a group
 * call, otherwise the peer. Threaded into the active phase so the chat
 * panel and mid-call invites all target the same channel. */
type ChatTarget = { id: string; type: "user" | "group" } | null;

/**
 * Global call handling — mounted once at the workspace root so an incoming
 * call can be answered regardless of which conversation is open.
 *
 * Architecture: raw Chat SDK for signalling + standalone Calls SDK for the
 * media session, with a fully custom Dispatch-themed UI. The UI Kit's
 * prebuilt call widgets were removed after extensive debugging — their
 * internal listener lifecycle never delivered accept/reject back to the
 * caller. Accept/decline/cancel are signalled over BOTH the SDK's call
 * events and transient messages (see callSdk.ts); whichever arrives first
 * transitions the state machine, duplicates are no-ops.
 */

type Phase =
  | { kind: "idle" }
  | {
      kind: "outgoing";
      sessionId: string;
      peerName: string;
      audioOnly: boolean;
      receiverId: string;
      receiverType: "user" | "group";
    }
  | { kind: "incoming"; call: CometChat.Call }
  /* Invited into an already-running call by a participant ("add people"). */
  | { kind: "invited"; invite: calls.CallInvite }
  | {
      kind: "active";
      sessionId: string;
      audioOnly: boolean;
      oneToOne: boolean;
      title: string;
      chatTarget: ChatTarget;
    };

type CallContextValue = {
  startCall: (
    receiverId: string,
    receiverType: "user" | "group",
    callType: "audio" | "video",
    receiverName?: string
  ) => void;
};

const CallContext = createContext<CallContextValue | null>(null);

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCall must be used within CallProvider");
  return ctx;
}

const CALL_LISTENER_ID = "dbm-call-listener";
const SIGNAL_LISTENER_ID = "dbm-call-signal-listener";
/** Give up on an unanswered outgoing call after this long. Long enough to
 * reach a device, short enough that a missed call fails fast instead of
 * ringing out. */
const RING_TIMEOUT_MS = 20_000;
/** Callee-side safety net for a caller whose tab died without sending the
 * cancel. Deliberately just past RING_TIMEOUT_MS so the real cancel
 * normally lands first and this never fires. */
const INCOMING_EXPIRY_MS = 25_000;
/** Mid-call join invites expire on their own — the call may well be over. */
const INVITE_EXPIRY_MS = 45_000;

/**
 * How long a non-active phase may sit before startCall treats it as
 * wedged and clears it instead of refusing with "already in a call".
 *
 * Every pre-active phase has its own timer that should return it to idle
 * long before this, so reaching it means something genuinely went wrong
 * (a dropped signal, a listener that never fired, a backgrounded tab
 * whose timers were throttled). Refusing forever in that state is the
 * worst outcome: the user sees no call anywhere, yet can't start one and
 * is told they're already in one. Generous enough never to fight a real
 * in-progress call, short enough to self-heal within one retry.
 */
const STALE_PHASE_MS = 60_000;

export function CallProvider({ children }: { children: ReactNode }) {
  const [phase, setPhaseState] = useState<Phase>({ kind: "idle" });
  /* Mirrors `phase` for the async listeners/callbacks below, which are
     registered once and would otherwise close over the phase as it was at
     mount. Written through setPhase (never during render — a render-phase
     ref write can be thrown away by a discarded concurrent render, leaving
     the ref describing a state that was never committed, which is exactly
     how "you're already in a call" starts appearing with no call on
     screen). */
  const phaseRef = useRef<Phase>({ kind: "idle" });
  /** When the current phase was entered — drives the staleness check. */
  const phaseSinceRef = useRef(Date.now());

  const setPhase = useCallback((next: Phase) => {
    phaseRef.current = next;
    phaseSinceRef.current = Date.now();
    setPhaseState(next);
  }, []);

  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  /** Set the moment a call goes active; read (and cleared) when it ends. */
  const callStartRef = useRef<number | null>(null);
  /* Guards the window between clicking call and the outgoing phase being
     set — placeCall is preceded by an async permission probe, so without a
     synchronous flag a double-click (or audio-then-video) starts two real
     calls, the second of which the callee auto-rejects as busy. */
  const startingRef = useRef(false);
  /* An accept/reject that arrived before its own outgoing phase existed.
     placeCall resolves over the network, so a fast callee can answer in
     the gap between the session existing server-side and this tab
     committing the outgoing phase — without buffering, that accept is
     dropped and the caller rings on at an already-answered call. */
  const earlySignalsRef = useRef(new Map<string, "accepted" | "rejected">());
  /* Only ever drained by the session it belongs to, so signals for calls
     that never became ours (a sibling tab's, a cancelled dial) would
     otherwise sit here for the life of the tab. The buffer only has to
     survive the few hundred ms between placeCall resolving and the phase
     committing, so anything beyond a couple of entries is already dead. */
  const rememberEarlySignal = useCallback((sessionId: string, action: "accepted" | "rejected") => {
    const buf = earlySignalsRef.current;
    if (buf.size > 8) buf.clear();
    buf.set(sessionId, action);
  }, []);

  const notify = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 3000);
  }, []);

  const goActiveFromOutgoing = useCallback(
    (sessionId: string) => {
      const current = phaseRef.current;
      if (current.kind !== "outgoing" || current.sessionId !== sessionId) {
        // Not outgoing yet — remember it so startCall can apply it the
        // moment the phase lands (see the buffer drain there).
        if (current.kind === "idle") rememberEarlySignal(sessionId, "accepted");
        return;
      }
      callStartRef.current = Date.now();
      sounds.playOnce(sounds.SOUND.callConnected);
      setPhase({
        kind: "active",
        sessionId,
        audioOnly: current.audioOnly,
        oneToOne: current.receiverType === "user",
        title: current.peerName,
        chatTarget: { id: current.receiverId, type: current.receiverType },
      });
    },
    [setPhase, rememberEarlySignal]
  );

  const outgoingEnded = useCallback(
    (sessionId: string, message: string) => {
      const current = phaseRef.current;
      if (current.kind !== "outgoing" || current.sessionId !== sessionId) {
        if (current.kind === "idle") rememberEarlySignal(sessionId, "rejected");
        return;
      }
      setPhase({ kind: "idle" });
      notify(message);
    },
    [notify, setPhase, rememberEarlySignal]
  );

  /**
   * Drops an incoming/invited card for a session that's over — and tells
   * every OTHER tab to do the same.
   *
   * The announce is the important half. Each tab holds its own CometChat
   * connection, and a cancellation (especially over the transient-message
   * fallback) frequently reaches only ONE of them. Previously only
   * locally-initiated outcomes (accept/decline/busy) were broadcast, so a
   * caller hanging up left every other tab ringing at a dead session until
   * its own timeout fired. Now whichever tab hears about it first relays
   * that to the rest, no matter how it found out.
   */
  const dismissIncoming = useCallback(
    (sessionId: string, message?: string) => {
      const current = phaseRef.current;
      const matches =
        (current.kind === "incoming" && current.call.getSessionId() === sessionId) ||
        (current.kind === "invited" && current.invite.sessionId === sessionId);
      calls.announceCallHandled(sessionId);
      if (!matches) return;
      setPhase({ kind: "idle" });
      if (message) notify(message);
    },
    [notify, setPhase]
  );

  // Signalling listeners — registered once, live for the whole session.
  useEffect(() => {
    CometChat.addCallListener(
      CALL_LISTENER_ID,
      new CometChat.CallListener({
        onIncomingCallReceived: (call: CometChat.Call) => {
          // A call I started myself can echo back to me as "incoming" —
          // most visibly with group calls, or in the brief gap before my
          // own outgoing phase commits (placeCall resolves over the
          // network). Never ring myself for my own call: that's exactly
          // the "it rings on my end when I add someone" symptom.
          const myUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
          if (myUid && call.getCallInitiator()?.getUid() === myUid) return;
          if (phaseRef.current.kind === "idle") {
            setPhase({ kind: "incoming", call });
          } else {
            void calls.rejectBusy(call.getSessionId());
            // Our other tabs are idle and will have popped the same call —
            // tell them it's been busy-rejected so they don't keep ringing
            // at a session that no longer exists.
            calls.announceCallHandled(call.getSessionId());
          }
        },
        onIncomingCallCancelled: (call: CometChat.Call) => {
          dismissIncoming(call.getSessionId(), "Call withdrawn before you answered");
        },
        onOutgoingCallAccepted: (call: CometChat.Call) => {
          goActiveFromOutgoing(call.getSessionId());
        },
        onOutgoingCallRejected: (call: CometChat.Call) => {
          outgoingEnded(
            call.getSessionId(),
            calls.wasBusy(call) ? "They're on another call" : "Call declined"
          );
        },
      })
    );

    // Redundant transport for the same three outcomes — see callSdk.ts.
    CometChat.addMessageListener(
      SIGNAL_LISTENER_ID,
      new CometChat.MessageListener({
        onTransientMessageReceived: (message: CometChat.TransientMessage) => {
          const invite = calls.parseCallInvite(message);
          if (invite) {
            // Only interrupt an idle user — never someone already on a call.
            if (phaseRef.current.kind === "idle") setPhase({ kind: "invited", invite });
            return;
          }
          const signal = calls.parseCallSignal(message);
          if (!signal) return;
          if (signal.action === "accepted") {
            goActiveFromOutgoing(signal.sessionId);
          } else if (signal.action === "rejected") {
            outgoingEnded(signal.sessionId, "Call declined");
          } else if (signal.action === "cancelled") {
            dismissIncoming(signal.sessionId, "Call withdrawn before you answered");
          }
        },
      })
    );

    return () => {
      CometChat.removeCallListener(CALL_LISTENER_ID);
      CometChat.removeMessageListener(SIGNAL_LISTENER_ID);
    };
  }, [goActiveFromOutgoing, outgoingEnded, dismissIncoming, notify, setPhase]);

  // Another of THIS user's tabs answered/declined/busy-rejected a session —
  // drop our card for it silently instead of ringing at a dead call.
  useEffect(() => {
    return calls.onCallHandledElsewhere((sessionId) => {
      const current = phaseRef.current;
      const matches =
        (current.kind === "incoming" && current.call.getSessionId() === sessionId) ||
        (current.kind === "invited" && current.invite.sessionId === sessionId);
      if (matches) setPhase({ kind: "idle" });
    });
  }, [setPhase]);

  /* Closing the tab mid-ring used to leave the callee's phone ringing at a
     call nobody was on any more, until their own expiry timer caught it.
     Sending the cancel on the way out ends it immediately instead — and
     leaving an active session cleanly stops the far end sitting in a call
     with a participant who silently vanished. */
  useEffect(() => {
    const onLeave = (e: PageTransitionEvent) => {
      // persisted === true means the page is being frozen into the back/
      // forward cache and can be restored intact — which is what happens
      // when a phone backgrounds the browser. Tearing the call down there
      // would hang up on someone for switching apps for a second.
      if (e.persisted) return;
      const current = phaseRef.current;
      if (current.kind === "outgoing") {
        calls.sendCallSignal(
          current.receiverId,
          current.receiverType,
          "cancelled",
          current.sessionId
        );
        void calls.cancelOutgoingCall(current.sessionId).catch(() => {});
      } else if (current.kind === "incoming") {
        void calls.declineCall(current.call.getSessionId()).catch(() => {});
      } else if (current.kind === "active") {
        calls.leaveSession();
        if (current.oneToOne) void calls.endCallRecord(current.sessionId).catch(() => {});
      }
    };
    window.addEventListener("pagehide", onLeave);
    return () => window.removeEventListener("pagehide", onLeave);
  }, []);

  // Ring tones follow the phase, with a distinct tone per side: the
  // callee hears the incoming ringtone, the caller hears their own
  // outgoing ringback. For incoming/invited, a cross-tab claim decides
  // ONE tab to ring audibly — every open tab still shows the answer
  // card, but only one of them makes noise.
  useEffect(() => {
    if (phase.kind === "incoming" || phase.kind === "invited") {
      const sessionId =
        phase.kind === "incoming" ? phase.call.getSessionId() : phase.invite.sessionId;
      if (calls.claimRing(sessionId)) {
        sounds.startLoop(sounds.SOUND.incomingCall);
      }
      return () => {
        sounds.stopLoop();
        calls.releaseRing(sessionId);
      };
    }
    if (phase.kind === "outgoing") {
      sounds.startLoop(sounds.SOUND.outgoingCall);
      return () => sounds.stopLoop();
    }
    calls.stopRing(); // safety net for any stray synthesized ring left over
    return () => calls.stopRing();
  }, [phase]);

  // An unanswered incoming call quietly expires — safety net for a caller
  // whose tab died mid-ring and never sent the cancel. Sits just past the
  // caller's own give-up so the normal cancel path wins the race, and
  // announces so sibling tabs stop at the same moment rather than each
  // waiting out its own timer.
  useEffect(() => {
    if (phase.kind !== "incoming") return;
    const sessionId = phase.call.getSessionId();
    const timer = window.setTimeout(() => dismissIncoming(sessionId), INCOMING_EXPIRY_MS);
    return () => window.clearTimeout(timer);
  }, [phase, dismissIncoming]);

  // Invites quietly expire if ignored — the call may well have ended.
  useEffect(() => {
    if (phase.kind !== "invited") return;
    const sessionId = phase.invite.sessionId;
    const timer = window.setTimeout(() => dismissIncoming(sessionId), INVITE_EXPIRY_MS);
    return () => window.clearTimeout(timer);
  }, [phase, dismissIncoming]);

  // Unanswered outgoing calls give up after RING_TIMEOUT_MS.
  useEffect(() => {
    if (phase.kind !== "outgoing") return;
    const { sessionId, receiverId, receiverType } = phase;
    const timer = window.setTimeout(() => {
      const current = phaseRef.current;
      if (current.kind === "outgoing" && current.sessionId === sessionId) {
        calls.sendCallSignal(receiverId, receiverType, "cancelled", sessionId);
        void calls.cancelOutgoingCall(sessionId).catch(() => {});
        setPhase({ kind: "idle" });
        notify("No answer");
      }
    }, RING_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [phase, notify, setPhase]);

  const startCall = useCallback(
    (
      receiverId: string,
      receiverType: "user" | "group",
      callType: "audio" | "video",
      receiverName?: string
    ) => {
      // Two separate guards, because they catch two different things: the
      // ref catches a genuinely in-progress call, the flag catches a second
      // click landing while the first click's permission probe is still
      // open (during which the phase is legitimately still idle).
      if (startingRef.current) return;

      const current = phaseRef.current;
      if (current.kind !== "idle") {
        const stale = Date.now() - phaseSinceRef.current > STALE_PHASE_MS;
        if (current.kind === "active" || !stale) {
          notify(
            current.kind === "active"
              ? "You're already in a call"
              : current.kind === "incoming"
              ? "Answer or decline the incoming call first"
              : "Finish the call you're setting up first"
          );
          return;
        }
        // Wedged in a pre-active phase well past its own timeout: clear it
        // and place the call rather than refusing forever (see STALE_PHASE_MS).
        console.warn("clearing a stale call phase", current.kind);
        sounds.stopLoop();
        setPhase({ kind: "idle" });
      }

      const audioOnly = callType === "audio";
      startingRef.current = true;
      const release = () => {
        startingRef.current = false;
      };

      // Prove the devices work BEFORE anyone's phone rings — a blocked mic
      // discovered mid-connect used to surface as a cryptic "not enabled"
      // inside the call stage, after the other person had already answered.
      void calls
        .ensureMediaAccess(audioOnly)
        .then((access) => {
          if (!access.ok) {
            notify(access.message);
            release();
            return;
          }
          // The permission prompt takes time — someone may have called US
          // while it was open. Never stack a second call on top.
          if (phaseRef.current.kind !== "idle") {
            release();
            return;
          }
          if (!audioOnly && !access.cameraAvailable) {
            notify("Camera unavailable — calling with microphone only");
          }
          return calls
            .placeCall(receiverId, receiverType, audioOnly)
            .then((call) => {
              const sessionId = call.getSessionId();
              setPhase({
                kind: "outgoing",
                sessionId,
                peerName: receiverName || receiverId,
                audioOnly,
                receiverId,
                receiverType,
              });
              release();
              // Drain anything that arrived while the call was still being
              // placed, so a very fast answer isn't silently swallowed.
              const early = earlySignalsRef.current.get(sessionId);
              if (early) {
                earlySignalsRef.current.delete(sessionId);
                if (early === "accepted") goActiveFromOutgoing(sessionId);
                else outgoingEnded(sessionId, "Call declined");
              }
            })
            .catch((e) => {
              console.warn("call initiate failed", e);
              notify(
                receiverType === "group"
                  ? "Couldn't start the call — you may no longer be in this channel"
                  : "Couldn't start the call — check your connection and try again"
              );
              release();
            });
        })
        .catch((e) => {
          // A rejected permission probe itself (rather than a denied
          // device) would otherwise leave startingRef stuck true, blocking
          // every later call attempt in this tab for good.
          console.warn("call preflight failed", e);
          notify("Couldn't start the call — try again");
          release();
        });
    },
    [notify, setPhase, goActiveFromOutgoing, outgoingEnded]
  );

  const accept = useCallback(async () => {
    const current = phaseRef.current;
    if (current.kind !== "incoming") return;
    const call = current.call;
    const sessionId = call.getSessionId();
    // Check the devices while the call is still ringing — if the mic is
    // blocked, the caller keeps ringing and we say what to fix, instead of
    // accepting into a session that immediately shows "not enabled".
    const audioOnly = call.getType() === CometChat.CALL_TYPE.AUDIO;
    const access = await calls.ensureMediaAccess(audioOnly);
    if (!access.ok) {
      notify(access.message);
      return;
    }
    if (!audioOnly && !access.cameraAvailable) {
      notify("Camera unavailable — joining with microphone only");
    }
    try {
      const accepted = await calls.acceptCall(sessionId);
      calls.announceCallHandled(sessionId); // other tabs: stop ringing, drop the card
      const initiatorUid = call.getCallInitiator()?.getUid();
      if (initiatorUid) calls.sendCallSignal(initiatorUid, "user", "accepted", sessionId);
      callStartRef.current = Date.now();
      sounds.playOnce(sounds.SOUND.callConnected);
      const acceptedGroup = accepted.getReceiverType() === CometChat.RECEIVER_TYPE.GROUP;
      // Group call → chat goes to the group; 1:1 → to the caller (the
      // receiver on an incoming 1:1 call is me, not the person to chat to).
      const initiatorUidForChat = call.getCallInitiator()?.getUid();
      const acceptedChatTarget: ChatTarget = acceptedGroup
        ? { id: accepted.getReceiverId(), type: "group" }
        : initiatorUidForChat
        ? { id: initiatorUidForChat, type: "user" }
        : null;
      setPhase({
        kind: "active",
        sessionId: accepted.getSessionId(),
        audioOnly: accepted.getType() === CometChat.CALL_TYPE.AUDIO,
        oneToOne: accepted.getReceiverType() === CometChat.RECEIVER_TYPE.USER,
        title: call.getCallInitiator()?.getName() ?? "Call",
        chatTarget: acceptedChatTarget,
      });
    } catch (e) {
      console.warn("accept failed", e);
      notify("Couldn't join — the call may have ended");
      // Announce too: if this failed because another tab already took it,
      // that tab's card is the one that should survive, not ours.
      calls.announceCallHandled(sessionId);
      setPhase({ kind: "idle" });
    }
  }, [notify, setPhase]);

  const decline = useCallback(async () => {
    const current = phaseRef.current;
    if (current.kind !== "incoming") return;
    const call = current.call;
    const sessionId = call.getSessionId();
    setPhase({ kind: "idle" });
    calls.announceCallHandled(sessionId); // other tabs: stop ringing, drop the card
    const initiatorUid = call.getCallInitiator()?.getUid();
    if (initiatorUid) calls.sendCallSignal(initiatorUid, "user", "rejected", sessionId);
    await calls.declineCall(sessionId).catch(() => {});
  }, [setPhase]);

  const cancelOutgoing = useCallback(async () => {
    const current = phaseRef.current;
    if (current.kind !== "outgoing") return;
    const { sessionId, receiverId, receiverType } = current;
    setPhase({ kind: "idle" });
    calls.sendCallSignal(receiverId, receiverType, "cancelled", sessionId);
    await calls.cancelOutgoingCall(sessionId).catch(() => {});
  }, [setPhase]);

  /** Join a running call you were invited into mid-flight — no
   * CometChat.Call handshake, straight into the media session. */
  const acceptInvite = useCallback(async () => {
    const current = phaseRef.current;
    if (current.kind !== "invited") return;
    const { invite } = current;
    const access = await calls.ensureMediaAccess(invite.audioOnly);
    if (!access.ok) {
      notify(access.message);
      return;
    }
    if (!invite.audioOnly && !access.cameraAvailable) {
      notify("Camera unavailable — joining with microphone only");
    }
    calls.announceCallHandled(invite.sessionId); // other tabs: drop the invite
    sounds.playOnce(sounds.SOUND.callConnected);
    // Post into the same channel the rest of the call uses (carried on the
    // invite); fall back to the inviter if an older client omitted it.
    const invitedChatTarget: ChatTarget =
      invite.chatTargetId && invite.chatTargetType
        ? { id: invite.chatTargetId, type: invite.chatTargetType }
        : invite.fromUid
        ? { id: invite.fromUid, type: "user" }
        : null;
    setPhase({
      kind: "active",
      sessionId: invite.sessionId,
      audioOnly: invite.audioOnly,
      oneToOne: false, // joined mid-call: never end the record for others
      title: invite.title,
      chatTarget: invitedChatTarget,
    });
  }, [notify, setPhase]);

  const api = useMemo(() => ({ startCall }), [startCall]);

  const incomingCall = phase.kind === "incoming" ? phase.call : null;
  const incomingName = incomingCall?.getCallInitiator()?.getName() ?? "Someone";
  const incomingAudio = incomingCall?.getType() === CometChat.CALL_TYPE.AUDIO;
  const incomingGroup = incomingCall?.getReceiverType() === CometChat.RECEIVER_TYPE.GROUP;

  const overlay = (
    <>
      {phase.kind === "outgoing" && (
        <div className="dbm-callveil">
          <div className="dbm-callcard">
            <div className="dbm-callcard__kicker">
              Calling · {phase.audioOnly ? "audio" : "video"}
            </div>
            <div className="dbm-callcard__halo dbm-callcard__halo--calm">
              {/* uid only for 1:1 — a group call's receiverId is a guid,
                  which has no avatar to look up. */}
              <Avatar
                size="dbm-callcard__avatar"
                uid={phase.receiverType === "user" ? phase.receiverId : undefined}
                name={phase.peerName}
                colored={false}
              />
            </div>
            <div className="dbm-callcard__name">{phase.peerName}</div>
            <div className="dbm-callcard__state">
              ringing<span className="dbm-callcard__ellip" />
            </div>
            <div className="dbm-callcard__row">
              <CallOrb kind="end" label="Cancel" onClick={() => void cancelOutgoing()}>
                <PhoneIcon size={22} />
              </CallOrb>
            </div>
          </div>
        </div>
      )}

      {phase.kind === "incoming" && (
        <div className="dbm-callveil">
          <div className="dbm-callcard">
            <div className="dbm-callcard__kicker">
              Incoming · {incomingGroup ? "group " : ""}
              {incomingAudio ? "audio" : "video"} call
            </div>
            <div className="dbm-callcard__halo">
              <Avatar
                size="dbm-callcard__avatar"
                uid={incomingCall?.getCallInitiator()?.getUid()}
                name={incomingName}
                colored={false}
              />
            </div>
            <div className="dbm-callcard__name">{incomingName}</div>
            <div className="dbm-callcard__state">
              wants to talk<span className="dbm-callcard__ellip" />
            </div>
            <div className="dbm-callcard__row">
              <CallOrb kind="end" label="Decline" onClick={() => void decline()}>
                <PhoneIcon size={22} />
              </CallOrb>
              <CallOrb kind="accept" label="Accept" onClick={() => void accept()}>
                {incomingAudio ? <PhoneIcon size={22} /> : <VideoIcon size={22} />}
              </CallOrb>
            </div>
          </div>
        </div>
      )}

      {phase.kind === "invited" && (
        <div className="dbm-callveil">
          <div className="dbm-callcard">
            <div className="dbm-callcard__kicker">
              Join call · {phase.invite.audioOnly ? "audio" : "video"}
            </div>
            <div className="dbm-callcard__halo">
              <Avatar
                size="dbm-callcard__avatar"
                uid={phase.invite.fromUid}
                name={phase.invite.fromName}
                colored={false}
              />
            </div>
            <div className="dbm-callcard__name">{phase.invite.fromName}</div>
            <div className="dbm-callcard__state">invited you to join "{phase.invite.title}"</div>
            <div className="dbm-callcard__row">
              <CallOrb kind="end" label="Not now" onClick={() => setPhase({ kind: "idle" })}>
                <PhoneIcon size={22} />
              </CallOrb>
              <CallOrb kind="accept" label="Join" onClick={() => void acceptInvite()}>
                {phase.invite.audioOnly ? <PhoneIcon size={22} /> : <VideoIcon size={22} />}
              </CallOrb>
            </div>
          </div>
        </div>
      )}

      {phase.kind === "active" && (
        <CallStage
          sessionId={phase.sessionId}
          audioOnly={phase.audioOnly}
          oneToOne={phase.oneToOne}
          title={phase.title}
          chatTarget={phase.chatTarget}
          notify={notify}
          onDone={() => {
            const startedAt = callStartRef.current;
            callStartRef.current = null;
            calls.playHangupTone();
            if (startedAt) notify(`Call ended · ${formatDuration((Date.now() - startedAt) / 1000)}`);
            setPhase({ kind: "idle" });
          }}
        />
      )}

      {toast && <div className="dbm-calltoast">{toast}</div>}
    </>
  );

  return (
    <CallContext.Provider value={api}>
      {children}
      {createPortal(overlay, document.body)}
    </CallContext.Provider>
  );
}

/** Round phone-style action button with its label underneath — the red
 * one carries a phone glyph rotated to "hung up" via CSS. */
function CallOrb({
  kind,
  label,
  onClick,
  children,
}: {
  kind: "accept" | "end";
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <div className="dbm-callaction">
      <button
        className={`dbm-callorb dbm-callorb--${kind}`}
        onClick={() => {
          sounds.playOnce(sounds.SOUND.click);
          onClick();
        }}
        aria-label={label}
      >
        {children}
      </button>
      <span className="dbm-callaction__label">{label}</span>
    </div>
  );
}

/** Full-screen media stage — the Calls SDK renders its call UI into the frame. */
function CallStage({
  sessionId,
  audioOnly,
  oneToOne,
  title,
  chatTarget,
  notify,
  onDone,
}: {
  sessionId: string;
  audioOnly: boolean;
  oneToOne: boolean;
  title: string;
  chatTarget: ChatTarget;
  notify: (msg: string) => void;
  onDone: () => void;
}) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const endedRef = useRef(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  // Minimized: shrinks the SAME frame (never unmounted/rejoined — see
  // below) into a small floating widget instead of covering the screen,
  // so the rest of the app is usable — the sidebar and chat — while a
  // call is still running. Its own tiny mute/camera/hangup row calls the
  // Calls SDK directly (setCallMuted/setCallVideoPaused in callSdk.ts),
  // independent of the default layout's own buttons, which are too
  // cramped to reliably tap once the frame is this small — given a solid
  // backdrop (see the CSS) so it reads as ONE control bar rather than
  // stacking on top of the default layout's own bottom bar.
  const [minimized, setMinimized] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoPaused, setVideoPaused] = useState(false);

  // Ephemeral in-call chat (see callSdk.ts). Everything here is local
  // state that dies with the call — nothing is persisted, so when this
  // component unmounts the whole conversation is simply gone.
  const meUser = CometChatUIKitLoginListener.getLoggedInUser();
  const meUid = meUser?.getUid() || "";
  const meName = meUser?.getName() || "You";
  const [chatMessages, setChatMessages] = useState<calls.CallChatMessage[]>([]);
  const [chatDraft, setChatDraft] = useState("");
  const chatListRef = useRef<HTMLDivElement | null>(null);
  // The listener is registered once and would otherwise close over a stale
  // `showChat` — a ref lets it decide "should this bump the unread badge?"
  // against the live value without re-subscribing on every toggle.
  const showChatRef = useRef(showChat);
  useEffect(() => {
    showChatRef.current = showChat;
    if (showChat) setUnreadChat(0);
  }, [showChat]);

  useEffect(() => {
    const listenerId = `dbm-callchat-${sessionId}`;
    CometChat.addMessageListener(
      listenerId,
      new CometChat.MessageListener({
        onTransientMessageReceived: (m: CometChat.TransientMessage) => {
          const cm = calls.parseCallChat(m);
          if (!cm || cm.sessionId !== sessionId || cm.fromUid === meUid) return;
          setChatMessages((prev) => [...prev, cm]);
          if (!showChatRef.current) {
            setUnreadChat((n) => n + 1);
            sounds.playOnce(sounds.SOUND.messageReceived);
          }
        },
      })
    );
    return () => CometChat.removeMessageListener(listenerId);
  }, [sessionId, meUid]);

  // Keep the newest message in view as the thread grows / the panel opens.
  useEffect(() => {
    if (showChat && chatListRef.current) {
      chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
    }
  }, [chatMessages, showChat]);

  const sendChat = () => {
    const text = chatDraft.trim();
    if (!text || !chatTarget) return;
    const msg: calls.CallChatMessage = {
      sessionId,
      text,
      fromName: meName,
      fromUid: meUid,
      ts: Date.now(),
    };
    calls.sendCallChat(chatTarget.id, chatTarget.type, msg);
    // Transient messages never echo to the sender, so add mine locally.
    setChatMessages((prev) => [...prev, msg]);
    setChatDraft("");
  };
  // Where the minimized widget sits after being dragged — null means "use
  // the CSS default corner." Persists across minimize/expand toggles for
  // the life of this call (the state lives here, not in CSS), so picking
  // it up again doesn't snap back to the corner. Width/height are locked
  // in from the widget's own on-screen size the moment a drag starts —
  // some breakpoints (phones) size it via `left/right` rather than a
  // fixed `width`, which would otherwise collapse to content size the
  // instant a drag sets an explicit `left` and clears `right`.
  const [dragPos, setDragPos] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const draggedRef = useRef(false);

  const clampToViewport = useCallback((x: number, y: number, w: number, h: number) => {
    const margin = 8;
    // On phones the rail becomes a bottom tab bar (see the ≤640px rules) —
    // without this, the widget could be dragged to sit half-hidden behind
    // it instead of stopping just above.
    const bottomReserve = window.innerWidth <= 640 ? 66 : 0;
    return {
      x: Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - w - margin)),
      y: Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - h - margin - bottomReserve)),
    };
  }, []);

  // Re-clamp on resize (rotating a phone, resizing the browser) so a
  // previously-dragged widget can't end up stranded off-screen.
  useEffect(() => {
    if (!dragPos) return;
    const onResize = () =>
      setDragPos((p) => (p ? { ...clampToViewport(p.x, p.y, p.w, p.h), w: p.w, h: p.h } : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [dragPos, clampToViewport]);

  const onMastPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (!minimized) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = stageRef.current?.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: rect?.left ?? 0,
      origY: rect?.top ?? 0,
    };
    draggedRef.current = false;
  };

  const onMastPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    // A few px of tolerance before it counts as a drag — otherwise a
    // plain tap-to-expand click (which naturally jitters a pixel or two)
    // would sometimes get eaten as a zero-distance "drag" instead.
    if (!draggedRef.current && Math.hypot(dx, dy) < 4) return;
    draggedRef.current = true;
    // Read the CURRENT size once, right as dragging actually begins, and
    // reuse it for the rest of this drag — not per-move, since after the
    // first move locks in an explicit width/height, re-reading the rect
    // would just measure that same locked-in size right back again.
    const rect = stageRef.current?.getBoundingClientRect();
    const w = dragPos?.w ?? rect?.width ?? 340;
    const h = dragPos?.h ?? rect?.height ?? 240;
    setDragPos({ ...clampToViewport(drag.origX + dx, drag.origY + dy, w, h), w, h });
  };

  const onMastPointerUp = () => {
    dragRef.current = null;
  };

  const finish = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    calls.leaveSession();
    if (oneToOne) void calls.endCallRecord(sessionId);
    onDone();
  }, [oneToOne, sessionId, onDone]);

  useEffect(() => {
    const container = frameRef.current;
    if (!container) return;

    calls
      .joinSession(sessionId, audioOnly, container, {
        onEnded: finish,
        onError: (message) => {
          notify(message);
          finish();
        },
        onPeerLeft: (remaining) => {
          // 1:1: the other person hung up, so there is no call left to sit
          // in — end it here too instead of leaving this side staring at
          // an empty stage until they notice and hang up manually.
          if (oneToOne && remaining === 0) finish();
        },
      })
      .then(() => {
        // The stage may have unmounted while the session was being joined.
        if (endedRef.current) calls.leaveSession();
      })
      .catch((e) => {
        console.warn("join session failed", e);
        notify("Couldn't join the call");
        finish();
      });

    return () => {
      if (!endedRef.current) {
        endedRef.current = true;
        calls.leaveSession();
        if (oneToOne) void calls.endCallRecord(sessionId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nudges the Calls SDK's own layout in case it relies on the window
  // resize event to notice its container changed size — a plain CSS
  // resize of `.dbm-callstage__frame` might not otherwise be enough for it
  // to relayout, which is what left its content looking wrong (clipped/
  // misaligned) after shrinking straight into the minimized widget.
  // Doesn't run until AFTER the class change has actually painted.
  const nudgeLayout = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    });
  };

  return (
    <div
      className={`dbm-callstage ${minimized ? "dbm-callstage--mini" : ""}`}
      ref={stageRef}
      style={
        minimized && dragPos
          ? { left: dragPos.x, top: dragPos.y, right: "auto", bottom: "auto", width: dragPos.w, height: dragPos.h }
          : undefined
      }
    >
      <header
        className="dbm-callstage__mast"
        onPointerDown={onMastPointerDown}
        onPointerMove={onMastPointerMove}
        onPointerUp={onMastPointerUp}
        onClick={() => {
          if (draggedRef.current) {
            draggedRef.current = false;
            return;
          }
          if (minimized) {
            setMinimized(false);
            nudgeLayout();
          }
        }}
      >
        <span className="dbm-callstage__mark" aria-hidden="true" />
        <span className="dbm-callstage__title">
          In call — {title} · {audioOnly ? "audio" : "video"}
        </span>
        {!minimized && (
          <div className="dbm-callstage__actions">
            {chatTarget && (
              <button
                className={`dbm-callstage__chatbtn ${showChat ? "on" : ""}`}
                title="In-call chat"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowChat((v) => !v);
                }}
              >
                <ChatIcon size={15} />
                <span>Chat</span>
                {unreadChat > 0 && !showChat && (
                  <span className="dbm-callstage__chatbadge">
                    {unreadChat > 9 ? "9+" : unreadChat}
                  </span>
                )}
              </button>
            )}
            <button
              className="dbm-callstage__add"
              onClick={(e) => {
                e.stopPropagation();
                setShowInvite(true);
              }}
            >
              <PersonPlusIcon size={15} />
              <span>Add people</span>
            </button>
          </div>
        )}
        <button
          className="dbm-callstage__min"
          title={minimized ? "Expand call" : "Minimize call"}
          aria-label={minimized ? "Expand call" : "Minimize call"}
          // Pointerdown bubbles to the mast's own drag-start handler
          // regardless of onClick's stopPropagation (different event
          // entirely) — without this, pressing this exact button could
          // register as the start of a drag instead of a plain click.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            sounds.playOnce(sounds.SOUND.click);
            setMinimized((v) => !v);
            nudgeLayout();
          }}
        >
          <ChevronIcon size={13} className={minimized ? "dbm-callstage__min-icon--up" : ""} />
        </button>
      </header>
      <div className="dbm-callstage__frame" ref={frameRef} />
      {showChat && !minimized && (
        <aside className="dbm-callstage__chat" onClick={(e) => e.stopPropagation()}>
          <div className="dbm-callstage__chat-head">
            <span>In-call chat</span>
            <button
              className="dbm-callstage__chat-close"
              aria-label="Close chat"
              onClick={() => setShowChat(false)}
            >
              ✕
            </button>
          </div>
          <div className="dbm-callstage__chat-note">
            Messages here are temporary — they disappear for everyone when the call ends.
          </div>
          <div className="dbm-callstage__chat-list" ref={chatListRef}>
            {chatMessages.length === 0 ? (
              <div className="dbm-callstage__chat-empty">No messages yet.</div>
            ) : (
              chatMessages.map((m, i) => (
                <div
                  key={`${m.ts}-${i}`}
                  className={`dbm-callstage__msg ${m.fromUid === meUid ? "mine" : ""}`}
                >
                  {m.fromUid !== meUid && (
                    <span className="dbm-callstage__msg-who">{m.fromName}</span>
                  )}
                  <span className="dbm-callstage__msg-text">{m.text}</span>
                </div>
              ))
            )}
          </div>
          <form
            className="dbm-callstage__chat-input"
            onSubmit={(e) => {
              e.preventDefault();
              sendChat();
            }}
          >
            <input
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              placeholder="Message…"
              aria-label="In-call message"
              autoFocus
            />
            <button type="submit" disabled={!chatDraft.trim()} aria-label="Send">
              <SendIcon size={16} />
            </button>
          </form>
        </aside>
      )}
      {minimized && (
        <div className="dbm-callstage__minicontrols">
          <button
            className={`dbm-callstage__minibtn ${muted ? "on" : ""}`}
            title={muted ? "Unmute" : "Mute"}
            aria-label={muted ? "Unmute" : "Mute"}
            onClick={(e) => {
              e.stopPropagation();
              const next = !muted;
              setMuted(next);
              calls.setCallMuted(next);
            }}
          >
            {muted ? <MicOffIcon size={15} /> : <MicIcon size={15} />}
          </button>
          {!audioOnly && (
            <button
              className={`dbm-callstage__minibtn ${videoPaused ? "on" : ""}`}
              title={videoPaused ? "Turn camera on" : "Turn camera off"}
              aria-label={videoPaused ? "Turn camera on" : "Turn camera off"}
              onClick={(e) => {
                e.stopPropagation();
                const next = !videoPaused;
                setVideoPaused(next);
                calls.setCallVideoPaused(next);
              }}
            >
              {videoPaused ? <CameraOffIcon size={15} /> : <VideoIcon size={15} />}
            </button>
          )}
          <button
            className="dbm-callstage__minibtn dbm-callstage__minibtn--end"
            title="Hang up"
            aria-label="Hang up"
            onClick={(e) => {
              e.stopPropagation();
              finish();
            }}
          >
            <PhoneIcon size={15} />
          </button>
        </div>
      )}
      {showInvite && (
        <InvitePicker
          sessionId={sessionId}
          audioOnly={audioOnly}
          title={title}
          chatTarget={chatTarget}
          notify={notify}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}

/** Mid-call "add people": pick users, each gets a join invite for THIS
 * running session over the transient channel (see callSdk.ts). */
function InvitePicker({
  sessionId,
  audioOnly,
  title,
  chatTarget,
  notify,
  onClose,
}: {
  sessionId: string;
  audioOnly: boolean;
  title: string;
  chatTarget: ChatTarget;
  notify: (msg: string) => void;
  onClose: () => void;
}) {
  const [users, setUsers] = useState<CometChat.User[]>([]);
  const [loading, setLoading] = useState(true);
  const [invited, setInvited] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    CometChat.getLoggedinUser()
      .then((me) => fetchVisibleUsers(me?.getUid()))
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((e) => console.warn("invite user list failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const invite = async (u: CometChat.User) => {
    const me = await CometChat.getLoggedinUser();
    calls.sendCallInvite(u.getUid(), {
      sessionId,
      audioOnly,
      title,
      fromName: me?.getName() || "A colleague",
      fromUid: me?.getUid(),
      // So the person joining posts their in-call chat into the same
      // channel everyone else on the call is using.
      chatTargetId: chatTarget?.id,
      chatTargetType: chatTarget?.type,
    });
    setInvited((prev) => new Set(prev).add(u.getUid()));
    notify(`Invite sent to ${u.getName()}`);
  };

  // Nothing is listed until you search — the point is to find one specific
  // person to add, not to browse the whole directory mid-call.
  const q = query.trim().toLowerCase();
  const matches = q
    ? users.filter(
        (u) =>
          (u.getName() || "").toLowerCase().includes(q) ||
          (emailForUid(u.getUid()) || parseMeta(u).dbmEmail || "").toLowerCase().includes(q)
      )
    : [];

  return (
    <div className="dbm-callveil" style={{ zIndex: 1002 }} onClick={onClose}>
      <div className="dbm-invite-card" onClick={(e) => e.stopPropagation()}>
        <div className="dbm-invite-card__head">
          <b>Add people to this call</b>
          <button className="dbm-invite-card__close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="dbm-invite-card__search">
          <input
            type="text"
            autoFocus
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="dbm-invite-card__list">
          {!q && (
            <div className="dbm-invite-card__empty">
              {loading ? "Loading people…" : "Start typing to find someone to add."}
            </div>
          )}
          {q && matches.length === 0 && (
            <div className="dbm-invite-card__empty">No one matches “{query.trim()}”.</div>
          )}
          {matches.map((u) => (
            <div className="dbm-invite-card__row" key={u.getUid()}>
              <span className="dbm-invite-card__name">{u.getName()}</span>
              <button
                className="dbm-invite-card__btn"
                disabled={invited.has(u.getUid())}
                onClick={() => void invite(u)}
              >
                {invited.has(u.getUid()) ? "Invited ✓" : "Invite"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
